import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { extractError } from '../../api/client';

/**
 * Factory for a content CRUD slice (bhajans / publications / gallery).
 * `api` must expose { list, create, update, remove } and, optionally,
 * `publish` / `unpublish` for publishable resources.
 * `keyField` names the id property used to match create/update/delete results
 * in the list (default 'slug' — set to 'id' for slug-less resources).
 */
export const createContentSlice = ({ name, api, hasPublish = false, keyField = 'slug' }) => {
  const fetchItems = createAsyncThunk(`${name}/fetchItems`, async (params = {}, { rejectWithValue }) => {
    try {
      const data = await api.list(params);
      return data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  });

  const createItem = createAsyncThunk(`${name}/createItem`, async ({ payload, onUploadProgress }, { rejectWithValue }) => {
    try {
      return await api.create(payload, { onUploadProgress });
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  });

  const updateItem = createAsyncThunk(`${name}/updateItem`, async ({ slug, payload, onUploadProgress }, { rejectWithValue }) => {
    try {
      return await api.update(slug, payload, { onUploadProgress });
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  });

  const deleteItem = createAsyncThunk(`${name}/deleteItem`, async (slug, { rejectWithValue }) => {
    try {
      await api.remove(slug);
      return slug;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  });

  const publishItem = hasPublish
    ? createAsyncThunk(`${name}/publishItem`, async (slug, { rejectWithValue }) => {
        try {
          return await api.publish(slug);
        } catch (error) {
          return rejectWithValue(extractError(error));
        }
      })
    : null;

  const unpublishItem = hasPublish
    ? createAsyncThunk(`${name}/unpublishItem`, async (slug, { rejectWithValue }) => {
        try {
          return await api.unpublish(slug);
        } catch (error) {
          return rejectWithValue(extractError(error));
        }
      })
    : null;

  const slice = createSlice({
    name,
    initialState: {
      items: [],
      count: 0,
      status: 'idle',
      error: null,
      submitting: false,
    },
    reducers: {
      clearContentError(state) {
        state.error = null;
      },
    },
    extraReducers: (builder) => {
      builder
        .addCase(fetchItems.pending, (state) => {
          state.status = 'loading';
        })
        .addCase(fetchItems.fulfilled, (state, action) => {
          state.status = 'succeeded';
          state.items = action.payload.results ?? action.payload;
          state.count = action.payload.count ?? state.items.length;
        })
        .addCase(fetchItems.rejected, (state, action) => {
          state.status = 'failed';
          state.error = action.payload?.message || 'Failed to load items';
        })
        .addCase(createItem.pending, (state) => {
          state.submitting = true;
        })
        .addCase(createItem.fulfilled, (state, action) => {
          state.submitting = false;
          state.items = [action.payload, ...state.items];
          state.count += 1;
        })
        .addCase(createItem.rejected, (state, action) => {
          state.submitting = false;
          state.error = action.payload?.message || 'Failed to create item';
        })
        .addCase(updateItem.pending, (state) => {
          state.submitting = true;
        })
        .addCase(updateItem.fulfilled, (state, action) => {
          state.submitting = false;
          state.items = state.items.map((it) => (it[keyField] === action.payload[keyField] ? action.payload : it));
        })
        .addCase(updateItem.rejected, (state, action) => {
          state.submitting = false;
          state.error = action.payload?.message || 'Failed to update item';
        })
        .addCase(deleteItem.pending, (state) => {
          state.submitting = true;
        })
        .addCase(deleteItem.fulfilled, (state, action) => {
          state.submitting = false;
          state.items = state.items.filter((it) => it[keyField] !== action.payload);
          state.count = Math.max(0, state.count - 1);
        })
        .addCase(deleteItem.rejected, (state, action) => {
          state.submitting = false;
          state.error = action.payload?.message || 'Failed to delete item';
        });

      if (publishItem) {
        builder
          .addCase(publishItem.fulfilled, (state, action) => {
            state.items = state.items.map((it) => (it.slug === action.payload.slug ? action.payload : it));
          })
          .addCase(publishItem.rejected, (state, action) => {
            state.error = action.payload?.message || 'Failed to publish item';
          });
      }
      if (unpublishItem) {
        builder
          .addCase(unpublishItem.fulfilled, (state, action) => {
            state.items = state.items.map((it) => (it.slug === action.payload.slug ? action.payload : it));
          })
          .addCase(unpublishItem.rejected, (state, action) => {
            state.error = action.payload?.message || 'Failed to unpublish item';
          });
      }
    },
  });

  const actions = {
    ...slice.actions,
    fetchItems,
    createItem,
    updateItem,
    deleteItem,
  };
  if (publishItem) actions.publishItem = publishItem;
  if (unpublishItem) actions.unpublishItem = unpublishItem;

  return { slice, actions };
};
